import { useEffect, useRef } from 'preact/hooks';
import { loadGoogleMapsApi } from '../../lib/load-google-maps.js';
import {
  bearingDegFromTo,
  featureCollectionBounds,
} from '../../lib/portal-geo.js';
import { findNearestStreetViewNearBoundary } from '../../lib/street-view-nearest.js';
import { createParcelPinOverlay } from '../../lib/street-view-parcel-pin.js';
import TerrainBandBackToMapButton from './TerrainBandBackToMapButton.jsx';

function renderMessage(el, text) {
  if (!el) return;
  el.innerHTML = '';
  const d = document.createElement('div');
  d.className = 'pp-detail-sv-split__msg pp-detail-empty';
  d.textContent = text;
  el.appendChild(d);
}

function triggerResize(mapsApi, target) {
  if (!mapsApi?.event || !target) return;
  try {
    mapsApi.event.trigger(target, 'resize');
  } catch {
    /* ignore */
  }
}

function scheduleResizes(mapsApi, targets, cancelledRef) {
  [0, 80, 200, 450, 900].forEach((ms) => {
    setTimeout(() => {
      if (cancelledRef()) return;
      (targets || []).forEach((t) => triggerResize(mapsApi, t));
    }, ms);
  });
}

function geoJsonToLatLngPaths(G, featureCollection) {
  const paths = [];
  const features = featureCollection?.features || [];
  features.forEach((feature) => {
    const geom = feature?.geometry;
    if (!geom) return;
    if (geom.type === 'Polygon' && Array.isArray(geom.coordinates?.[0])) {
      paths.push(geom.coordinates[0].map((c) => new G.LatLng(Number(c[1]), Number(c[0]))));
    } else if (geom.type === 'MultiPolygon') {
      (geom.coordinates || []).forEach((poly) => {
        if (Array.isArray(poly?.[0])) {
          paths.push(poly[0].map((c) => new G.LatLng(Number(c[1]), Number(c[0]))));
        }
      });
    }
  });
  return paths.filter((p) => p.length >= 3);
}

function fitMapToParcelAndCamera(G, map, parcelFc, cameraLL) {
  if (!map) return;
  const bounds = new G.LatLngBounds();
  let has = false;
  const box = featureCollectionBounds(parcelFc);
  if (box) {
    bounds.extend(new G.LatLng(box[0][1], box[0][0]));
    bounds.extend(new G.LatLng(box[1][1], box[1][0]));
    has = true;
  }
  if (cameraLL) {
    bounds.extend(cameraLL);
    has = true;
  }
  if (has) {
    map.fitBounds(bounds, { top: 28, right: 28, bottom: 28, left: 28 });
  }
}

/**
 * Terrain band Street View overlay — Mapbox altında mount kalır (eğim/yol gibi).
 * sol %40 — Google harita + parsel + mavi kamera noktası
 * sağ %60 — panorama + parsel iğnesi
 */
export function TerrainBandStreetViewSplit({
  open = false,
  parcelCenter = null,
  parcelFeatureCollection = null,
  onClose,
}) {
  const rootRef = useRef(null);
  const mapHostRef = useRef(null);
  const panoHostRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const mapHost = mapHostRef.current;
    const panoHost = panoHostRef.current;
    if (
      !mapHost ||
      !panoHost ||
      !parcelCenter ||
      typeof parcelCenter.lat !== 'number' ||
      typeof parcelCenter.lng !== 'number'
    ) {
      return undefined;
    }

    const panoramaRef = { current: null };
    const mapRef = { current: null };
    const overlayRef = { current: null };
    const parcelPolysRef = { current: [] };
    const cameraMarkerRef = { current: null };
    const headingLineRef = { current: null };
    const resizeObserverRef = { current: null };
    let cancelled = false;
    const isCancelled = () => cancelled;

    mapHost.innerHTML = '';
    panoHost.innerHTML = '';
    const loading = document.createElement('div');
    loading.className = 'pp-detail-sv-split__msg pp-detail-empty';
    loading.textContent = 'Arazi sınırına yakın Street View görüntüsü aranıyor…';
    panoHost.appendChild(loading);

    loadGoogleMapsApi()
      .then(() => {
        if (cancelled || !mapHostRef.current || !panoHostRef.current) return;
        const G = window.google.maps;
        if (!G?.StreetViewPanorama || !G?.StreetViewService || !G?.Map) {
          renderMessage(panoHost, 'Google Haritalar API yüklenemedi.');
          return;
        }

        const parcelLL = new G.LatLng(parcelCenter.lat, parcelCenter.lng);

        const map = new G.Map(mapHost, {
          center: parcelLL,
          zoom: 17,
          mapTypeId: 'hybrid',
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'greedy',
          clickableIcons: false,
        });
        mapRef.current = map;

        const paths = geoJsonToLatLngPaths(G, parcelFeatureCollection);
        paths.forEach((path) => {
          const poly = new G.Polygon({
            paths: path,
            strokeColor: '#ef4444',
            strokeOpacity: 0.95,
            strokeWeight: 2,
            fillColor: '#ef4444',
            fillOpacity: 0.18,
            map,
            clickable: false,
          });
          parcelPolysRef.current.push(poly);
        });

        const cameraMarker = new G.Marker({
          map,
          position: parcelLL,
          clickable: false,
          icon: {
            path: G.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: '#2563eb',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
          zIndex: 10,
        });
        cameraMarkerRef.current = cameraMarker;

        const syncCameraVisuals = (panorama) => {
          const pos = panorama?.getPosition?.();
          if (!pos) return;
          cameraMarker.setPosition(pos);
          const pov = panorama.getPov?.() || {};
          const heading = Number(pov.heading) || 0;
          const tip = G.geometry?.spherical
            ? G.geometry.spherical.computeOffset(pos, 28, heading)
            : null;
          if (headingLineRef.current) {
            headingLineRef.current.setMap(null);
            headingLineRef.current = null;
          }
          if (tip) {
            headingLineRef.current = new G.Polyline({
              map,
              path: [pos, tip],
              strokeColor: '#2563eb',
              strokeOpacity: 0.9,
              strokeWeight: 3,
              clickable: false,
              zIndex: 9,
            });
          }
          fitMapToParcelAndCamera(G, map, parcelFeatureCollection, pos);
        };

        const attachPanorama = (svData) => {
          if (cancelled || !panoHostRef.current) return;
          panoHost.innerHTML = '';
          const panoEl = document.createElement('div');
          panoEl.className = 'pp-detail-sv-split__pano-inner';
          panoHost.appendChild(panoEl);

          const pos = svData.location.latLng;
          let heading = bearingDegFromTo(pos.lat(), pos.lng(), parcelCenter.lat, parcelCenter.lng);
          if (G.geometry?.spherical) {
            heading = G.geometry.spherical.computeHeading(pos, parcelLL);
          }

          const panorama = new G.StreetViewPanorama(panoEl, {
            position: pos,
            pov: { heading, pitch: 5 },
            zoom: 1,
            pano: svData.location.pano,
            motionTracking: false,
            visible: true,
            addressControl: false,
            fullscreenControl: false,
            linksControl: true,
          });
          panoramaRef.current = panorama;
          map.setStreetView(panorama);

          const onReady = () => {
            if (cancelled) return;
            let st = 'OK';
            try {
              st = typeof panorama.getStatus === 'function' ? panorama.getStatus() : 'OK';
            } catch {
              st = 'OK';
            }
            if (st === 'OK') {
              if (!overlayRef.current) {
                overlayRef.current = createParcelPinOverlay(G, parcelLL, panorama);
              }
              syncCameraVisuals(panorama);
              triggerResize(G, panorama);
              triggerResize(G, map);
              scheduleResizes(G, [panorama, map], isCancelled);
              if (typeof ResizeObserver !== 'undefined') {
                resizeObserverRef.current?.disconnect();
                resizeObserverRef.current = new ResizeObserver(() => {
                  if (cancelled) return;
                  triggerResize(G, panorama);
                  triggerResize(G, map);
                });
                if (rootRef.current) resizeObserverRef.current.observe(rootRef.current);
                resizeObserverRef.current.observe(panoEl);
                resizeObserverRef.current.observe(mapHost);
              }
              return;
            }
            if (st == null || st === '' || st === 'UNKNOWN') return;
            renderMessage(panoHost, st === 'ZERO_RESULTS'
              ? 'Bu konum için Street View görüntüsü yok.'
              : `Street View yüklenemedi (${st || 'Bilinmiyor'}).`);
          };

          G.event.addListener(panorama, 'status_changed', onReady);
          G.event.addListener(panorama, 'position_changed', () => syncCameraVisuals(panorama));
          G.event.addListener(panorama, 'pov_changed', () => syncCameraVisuals(panorama));
          setTimeout(onReady, 0);
        };

        findNearestStreetViewNearBoundary(G, {
          parcelCenter,
          parcelFeatureCollection,
          isCancelled: () => cancelled,
        }).then((found) => {
          if (cancelled) return;
          if (found?.data) {
            attachPanorama(found.data);
          } else {
            renderMessage(panoHost, 'Arazi sınırı ve çevresinde (500 m) Street View görüntüsü yok.');
            fitMapToParcelAndCamera(G, map, parcelFeatureCollection, parcelLL);
          }
        });
      })
      .catch(() => {
        if (!cancelled && panoHost) {
          renderMessage(panoHost, 'Google Haritalar yüklenemedi. Ağ veya API anahtarını kontrol edin.');
        }
      });

    return () => {
      cancelled = true;
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      const G = window.google?.maps;
      if (G?.event && panoramaRef.current) {
        try { G.event.clearInstanceListeners(panoramaRef.current); } catch { /* ignore */ }
      }
      if (typeof overlayRef.current?.setMap === 'function') {
        try { overlayRef.current.setMap(null); } catch { /* ignore */ }
      }
      if (typeof panoramaRef.current?.setVisible === 'function') {
        try { panoramaRef.current.setVisible(false); } catch { /* ignore */ }
      }
      parcelPolysRef.current.forEach((p) => {
        try { p.setMap(null); } catch { /* ignore */ }
      });
      parcelPolysRef.current = [];
      if (cameraMarkerRef.current) {
        try { cameraMarkerRef.current.setMap(null); } catch { /* ignore */ }
      }
      if (headingLineRef.current) {
        try { headingLineRef.current.setMap(null); } catch { /* ignore */ }
      }
      if (mapRef.current) {
        try { mapRef.current.setStreetView(null); } catch { /* ignore */ }
      }
      panoramaRef.current = null;
      mapRef.current = null;
      overlayRef.current = null;
      cameraMarkerRef.current = null;
      headingLineRef.current = null;
      if (mapHost) mapHost.innerHTML = '';
      if (panoHost) panoHost.innerHTML = '';
    };
  }, [open, parcelCenter?.lat, parcelCenter?.lng, parcelFeatureCollection]);

  if (!open) return null;

  if (!parcelCenter) {
    return (
      <div
        className="pp-detail-terrain-band__mesh-expand pp-detail-sv-split"
        role="dialog"
        aria-modal="true"
        aria-label="sokak görüntüsü"
      >
        <div className="pp-detail-terrain-band__mesh-expand-bar">
          <span className="pp-detail-terrain-band__mesh-expand-title">sokak görüntüsü</span>
          <div className="pp-detail-terrain-band__mesh-expand-actions">
            <TerrainBandBackToMapButton onClick={onClose} />
          </div>
        </div>
        <div className="pp-detail-sv-split__msg pp-detail-empty">Parsel konumu bulunamadı.</div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className="pp-detail-terrain-band__mesh-expand pp-detail-sv-split"
      role="dialog"
      aria-modal="true"
      aria-label="sokak görüntüsü"
    >
      <div className="pp-detail-terrain-band__mesh-expand-bar">
        <span className="pp-detail-terrain-band__mesh-expand-title">sokak görüntüsü</span>
        <div className="pp-detail-terrain-band__mesh-expand-actions">
          <TerrainBandBackToMapButton onClick={onClose} />
        </div>
      </div>
      <div className="pp-detail-sv-split__body">
        <div className="pp-detail-sv-split__map" ref={mapHostRef} aria-label="Kamera konumu haritası" />
        <div className="pp-detail-sv-split__pano" ref={panoHostRef} aria-label="Street View panosu" />
      </div>
    </div>
  );
}

export default TerrainBandStreetViewSplit;
